/**
 * Blog / Ratgeber: client-safe types and pure helpers.
 *
 * Articles, categories and authors come from the futuredoctor Blog CMS and are
 * fetched once per build (scripts/blog/fetch-snapshot.mjs). The data itself is
 * read in lib/blog-data.ts, which is server-only — import that from server
 * components, and this file from anywhere.
 */

import { GOOGLEBOT_PREVIEW } from "@/lib/site";

export type {
  BlogArticle,
  BlogAuthor,
  BlogCardData,
  BlogCategoryInfo,
  BlogFaq,
  BlogSection,
} from "@/lib/blog-types";

/** Badge-/Akzentfarben je Kategorie-Slug – konsistent zum Ratgeber-Menü (CMS liefert color: null). */
const CATEGORY_THEMES: Record<string, { badge: string; text: string }> = {
  zulassung: { badge: "bg-mint-soft text-mint-deep", text: "text-mint-deep" },
  zulassungsrechner: { badge: "bg-accent-soft text-accent-deep", text: "text-accent" },
  bewerbung: { badge: "bg-amber-soft text-warn", text: "text-warn" },
};
const FALLBACK_THEME = { badge: "bg-accent-soft text-accent-deep", text: "text-accent" };

export function categoryTheme(slug: string): { badge: string; text: string } {
  return CATEGORY_THEMES[slug] ?? FALLBACK_THEME;
}

/** RSS 2.0 feed of all Ratgeber articles (app/ratgeber/feed.xml/route.ts). */
export const FEED_PATH = "/ratgeber/feed.xml";
export const FEED_TITLE = "ncrechner Ratgeber";

/**
 * `alternates` für Blog-Seiten: Canonical, hreflang (Seite ist nur deutsch →
 * de-DE + x-default auf sich selbst) und <link rel="alternate"> zum RSS-Feed.
 */
export function blogAlternates(url: string, siteUrl: string) {
  return {
    canonical: url,
    languages: { "de-DE": url, "x-default": url },
    types: { "application/rss+xml": [{ url: `${siteUrl}${FEED_PATH}`, title: FEED_TITLE }] },
  };
}

/**
 * Häufigste Tags einer Artikelliste (für die keywords einer Kategorie-/Autorenseite,
 * statt der seitenweiten Default-Keywords aus app/layout.tsx).
 */
export function topTags(articles: { tags: string[] }[], count = 10): string[] {
  // Case-insensitive („Hochschulstart" = „hochschulstart"); erste Schreibweise gewinnt.
  const freq = new Map<string, { tag: string; n: number }>();
  for (const a of articles) {
    for (const tag of a.tags) {
      const key = tag.trim().toLowerCase();
      const entry = freq.get(key) ?? { tag: tag.trim(), n: 0 };
      entry.n++;
      freq.set(key, entry);
    }
  }
  return [...freq.values()]
    .sort((a, b) => b.n - a.n)
    .slice(0, count)
    .map((e) => e.tag);
}

/** /ratgeber/{category}/ */
export function categoryPath(slug: string): string {
  return `/ratgeber/${slug}/`;
}

/** /ratgeber/{category}/{slug}/ */
export function articlePath(article: { slug: string; category: string }): string {
  return `/ratgeber/${article.category}/${article.slug}/`;
}

const DATE_LABEL = new Intl.DateTimeFormat("de-DE", {
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "Europe/Berlin",
});

/** ISO-Datum → Anzeigedatum, z. B. „18. Juni 2026". */
export function formatDateLabel(iso: string): string {
  return DATE_LABEL.format(new Date(iso));
}

/** true, wenn zwei ISO-Zeitpunkte auf denselben Kalendertag (Berlin) fallen. */
export function sameDay(a: string, b: string): boolean {
  return formatDateLabel(a) === formatDateLabel(b);
}

const TITLE_BRAND = " | ncrechner";

/**
 * Kompakter <title> (Ziel ≤ 60 Zeichen) für Suchmaschinen.
 * Nutzt den CMS-`metaTitle`, sonst den Kernteil des Titels vor „:", „ – " bzw. „?",
 * und hängt – wenn es passt – die Marke an. So bleibt der lange Anzeigetitel
 * auf der Seite erhalten, während der Head-Tag knapp bleibt.
 */
export function seoTitleFor(article: { title: string; seo?: { title?: string } }): string {
  const cms = article.seo?.title;
  if (cms) return (cms + TITLE_BRAND).length <= 60 ? cms + TITLE_BRAND : cms;
  const t = article.title;
  const colon = t.indexOf(":");
  const dash = t.indexOf(" – ");
  const q = t.indexOf("?");
  const cuts = [colon, dash, q].filter((i) => i > 0);
  let lead = t;
  if (cuts.length) {
    const cut = Math.min(...cuts);
    lead = (q === cut ? t.slice(0, cut + 1) : t.slice(0, cut)).trim();
  }
  if ((lead + TITLE_BRAND).length <= 60) return lead + TITLE_BRAND;
  if (lead.length <= 60) return lead;
  const words = lead.slice(0, 60).split(" ");
  words.pop();
  return words.join(" ");
}

/**
 * Meta-Description (Ziel ≤ 155 Zeichen). Nutzt die CMS-`metaDescription`,
 * sonst den `excerpt`; ist der Text zu lang (auch der aus dem CMS – dort steht
 * teils die komplette Bio), wird an einer Satz- bzw. Wortgrenze gekürzt.
 * Der volle `excerpt` bleibt für Karten/Hero erhalten.
 */
export function metaDescriptionFor(article: {
  excerpt: string;
  seo?: { description?: string };
}): string {
  const e = (article.seo?.description || article.excerpt).replace(/\s+/g, " ").trim();
  if (e.length <= 158) return e;
  const slice = e.slice(0, 155);
  const punct = Math.max(
    slice.lastIndexOf(". "),
    slice.lastIndexOf("! "),
    slice.lastIndexOf("? "),
  );
  if (punct >= 110) return e.slice(0, punct + 1).trim();
  const sp = slice.lastIndexOf(" ");
  return `${e.slice(0, sp).trim()} …`;
}

/** CMS `robotsMeta` ("index,follow" / "noindex,nofollow" …) → Next.js `robots`. */
export function robotsFor(robots: string | undefined) {
  const flags = (robots ?? "").toLowerCase().split(",").map((s) => s.trim());
  const index = !flags.includes("noindex");
  const follow = !flags.includes("nofollow");
  return {
    index,
    follow,
    ...(index ? { googleBot: { index, follow, ...GOOGLEBOT_PREVIEW } } : {}),
  };
}

const IMAGE_TYPES: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  avif: "image/avif",
  gif: "image/gif",
};

/** MIME-Typ aus der Dateiendung einer Bild-URL (für og:image:type), sonst undefined. */
export function imageTypeFor(url: string): string | undefined {
  const ext = url.split(/[?#]/)[0].split(".").pop()?.toLowerCase();
  return ext ? IMAGE_TYPES[ext] : undefined;
}

const TWITTER_CARDS = ["summary", "summary_large_image", "app", "player"] as const;
type TwitterCard = (typeof TWITTER_CARDS)[number];

/** CMS `twitterCard` → a card type Next.js accepts (default: summary_large_image). */
export function twitterCardFor(card: string | undefined): TwitterCard {
  return (TWITTER_CARDS as readonly string[]).includes(card ?? "")
    ? (card as TwitterCard)
    : "summary_large_image";
}
